// @catalog-federation — the keys a certificate or request names
// ============================================================================
// The TypeScript twin of libs/federation/CertifiedKeys.cs: the key schema of
// each defined service kind (D7), shared by the certificate request here and
// by the `service_certificate` body in C#, so the two can never disagree about
// what a kind certifies.

import { type JsonObject, type StrictObject, dictionary, toBase64 } from './json';
import {
    type SigningProfile, ES256_PROFILE, HYBRID_PROFILE, profileJson, sameProfile, validatePublicKey,
} from './signatures';
import { CertifiedKeyNames, ServiceKinds, type DefinedServiceKind } from './records';

/** One key a certificate vouches for; `kid` is carried by `cid.jwt` only. */
export interface CertifiedKey {
    publicKey: Uint8Array;
    signingProfile: SigningProfile;
    kid?: string;
}

/** Key name → key. */
export type CertifiedKeys = Readonly<Record<string, CertifiedKey>>;

const KID_RE = /^[A-Za-z0-9._:-]{1,128}$/;

/** The key names `kind` certifies, in canonical order. A kind this release
 *  defines no key schema for throws: it never borrows another kind's names. */
export function certifiedKeyNames(kind: DefinedServiceKind): readonly string[] {
    switch (kind) {
        case ServiceKinds.Cpr:
            return [CertifiedKeyNames.Block];
        case ServiceKinds.Cid:
            return [CertifiedKeyNames.Jwt, CertifiedKeyNames.Sync];
        default: {
            const unknown: never = kind;
            throw new Error(`no key schema for service kind '${String(unknown)}'`);
        }
    }
}

/** The one profile each certified key must use: ES256 for `jwt`, hybrid otherwise. */
export function certifiedKeyProfile(keyName: string): SigningProfile {
    return keyName === CertifiedKeyNames.Jwt ? ES256_PROFILE : HYBRID_PROFILE;
}

export function isValidKid(kid: unknown): kid is string {
    return typeof kid === 'string' && KID_RE.test(kid);
}

/** The `keys` object as payloads carry it. */
export function certifiedKeysJson(keys: CertifiedKeys): JsonObject {
    const o = dictionary<JsonObject[string]>();
    for (const [name, key] of Object.entries(keys)) {
        const entry: JsonObject = {
            public_key: toBase64(key.publicKey),
            signing_profile: profileJson(key.signingProfile),
        };
        if (key.kid !== undefined) entry['kid'] = key.kid;
        o[name] = entry;
    }
    return o;
}

/**
 * Reads the `keys` object of a request for a defined `kind`: exactly the
 * kind's key names, each with its profile and a well-formed public key; `kid`
 * on `jwt` (required) and nowhere else.
 */
export function readCertifiedKeys(keys: StrictObject, kind: DefinedServiceKind): Record<string, CertifiedKey> {
    const required = certifiedKeyNames(kind);
    for (const name of keys.names()) {
        if (!required.includes(name)) {
            throw keys.fail(`a ${kind} certificate certifies only ${required.join(', ')}; unknown key '${name}'`);
        }
    }

    const result = dictionary<CertifiedKey>();
    for (const name of required) {
        if (!keys.has(name)) throw keys.fail(`a ${kind} certificate must certify '${name}'`);
        const k = keys.object(name);
        const publicKey = k.base64('public_key');
        const signingProfile = k.profile('signing_profile');
        const expected = certifiedKeyProfile(name);
        if (!sameProfile(signingProfile, expected)) {
            throw k.fail(`'${name}' must use ${expected.algorithm}/${expected.parameterSet}; `
                + `got ${signingProfile.algorithm}/${signingProfile.parameterSet}`);
        }
        const defect = validatePublicKey(signingProfile, publicKey);
        if (defect !== null) throw k.fail(defect);

        const key: CertifiedKey = { publicKey, signingProfile };
        if (name === CertifiedKeyNames.Jwt) {
            if (!k.has('kid')) throw k.fail("the jwt key must carry its 'kid'");
            const kid = k.string('kid', 128);
            if (!isValidKid(kid)) throw k.fail(`'kid' must match [A-Za-z0-9._:-]{1,128}; got '${kid}'`);
            key.kid = kid;
        }
        k.end();
        result[name] = key;
    }
    keys.end();
    return result;
}
